import React, { useState } from 'react';
import api from '../services/api';

const ProductResearch = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  
  const [researchForm, setResearchForm] = useState({
    product_name: '',
    industry: '',
    task_type: 'comprehensive_research'
  });

  const handleResearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await api.post('/marketing/ai-agents/enhanced/product-research', {
        task_type: researchForm.task_type,
        product_name: researchForm.product_name,
        industry: researchForm.industry
      });
      
      setResults(response.data);
      setActiveTab('insights');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete research');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => window.location.href = '/'}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Product Research
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🔍 Product Intelligence & Research
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              AI-powered market research and competitive product analysis
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '0',
            gap: 'var(--space-1)'
          }}>
            {[
              { key: 'overview', label: '📊 Overview' },
              { key: 'products', label: '📦 Products' },
              { key: 'competitors', label: '🎯 Competitors' },
              { key: 'trends', label: '📈 Market Trends' },
              { key: 'insights', label: '💡 AI Insights' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'none',
                  border: 'none',
                  borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-500)',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ color: 'var(--gray-900)', marginBottom: '1rem' }}>Start Product Research</h3>
              
              {error && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#FEE2E2',
                  color: '#991B1B',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem'
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleResearch}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={researchForm.product_name}
                      onChange={(e) => setResearchForm({ ...researchForm, product_name: e.target.value })}
                      className="form-input"
                      placeholder="e.g., Wireless Earbuds Pro"
                      required
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                      Industry *
                    </label>
                    <input
                      type="text"
                      value={researchForm.industry}
                      onChange={(e) => setResearchForm({ ...researchForm, industry: e.target.value })}
                      className="form-input"
                      placeholder="e.g., Consumer Electronics"
                      required
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--gray-700)' }}>
                    Research Type *
                  </label>
                  <select
                    value={researchForm.task_type}
                    onChange={(e) => setResearchForm({ ...researchForm, task_type: e.target.value })}
                    className="form-input"
                  >
                    <option value="comprehensive_research">Comprehensive Market Research</option>
                    <option value="competitor_analysis">Competitor Analysis</option>
                    <option value="market_opportunity">Market Opportunity Analysis</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ width: '100%' }}
                >
                  {loading ? '🔍 Researching...' : '🚀 Start AI Research'}
                </button>
              </form>
            </div>

            <div className="card-sm" style={{ backgroundColor: 'var(--gray-50)' }}>
              <h4 style={{ color: 'var(--gray-900)', marginBottom: '0.5rem' }}>What You'll Get:</h4>
              <ul style={{ color: 'var(--gray-600)', paddingLeft: '1.5rem' }}>
                <li>AI-powered market analysis</li>
                <li>Competitor intelligence with web scraping</li>
                <li>Customer persona generation</li>
                <li>Actionable business insights</li>
                <li>30-day and 3-6 month action plans</li>
                <li>Success metrics and KPIs</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'insights' && results && (
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <button
                onClick={() => setActiveTab('overview')}
                className="btn btn-secondary"
              >
                ← New Research
              </button>
            </div>

            {results.success ? (
              <div>
                <div className="card-sm" style={{ marginBottom: '1rem', backgroundColor: '#D1FAE5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>✅</span>
                    <div>
                      <strong style={{ color: '#065F46' }}>Research Complete</strong>
                      <p style={{ margin: 0, color: '#047857', fontSize: '0.875rem' }}>
                        Agent: {results.agent || 'Product Research Agent'}
                      </p>
                    </div>
                  </div>
                </div>

                {results.results && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {results.results.ai_research && (
                      <div className="card-sm">
                        <h3 style={{ color: 'var(--gray-900)', marginBottom: '1rem' }}>
                          🤖 AI Market Analysis
                        </h3>
                        <pre style={{
                          backgroundColor: 'var(--gray-50)',
                          padding: '1rem',
                          borderRadius: '0.5rem',
                          overflow: 'auto',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {JSON.stringify(results.results.ai_research, null, 2)}
                        </pre>
                      </div>
                    )}

                    {results.results.web_research && (
                      <div className="card-sm">
                        <h3 style={{ color: 'var(--gray-900)', marginBottom: '1rem' }}>
                          🌐 Web Research Data
                        </h3>
                        <pre style={{
                          backgroundColor: 'var(--gray-50)',
                          padding: '1rem',
                          borderRadius: '0.5rem',
                          overflow: 'auto',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {JSON.stringify(results.results.web_research, null, 2)}
                        </pre>
                      </div>
                    )}

                    {results.results.insights && (
                      <div className="card-sm">
                        <h3 style={{ color: 'var(--gray-900)', marginBottom: '1rem' }}>
                          💡 Actionable Insights
                        </h3>
                        <pre style={{
                          backgroundColor: 'var(--gray-50)',
                          padding: '1rem',
                          borderRadius: '0.5rem',
                          overflow: 'auto',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {JSON.stringify(results.results.insights, null, 2)}
                        </pre>
                      </div>
                    )}

                    {results.results.confidence_score && (
                      <div className="card-sm">
                        <h4 style={{ color: 'var(--gray-900)', marginBottom: '0.5rem' }}>
                          Confidence Score
                        </h4>
                        <div style={{
                          height: '20px',
                          backgroundColor: 'var(--gray-200)',
                          borderRadius: '10px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: `${results.results.confidence_score * 100}%`,
                            backgroundColor: results.results.confidence_score > 0.7 ? '#10B981' : results.results.confidence_score > 0.5 ? '#F59E0B' : '#EF4444',
                            transition: 'width 0.3s'
                          }} />
                        </div>
                        <p style={{ marginTop: '0.5rem', color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                          {(results.results.confidence_score * 100).toFixed(1)}% confidence
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="card-sm" style={{ backgroundColor: '#FEE2E2' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>❌</span>
                  <div>
                    <strong style={{ color: '#991B1B' }}>Research Failed</strong>
                    <p style={{ margin: 0, color: '#DC2626', fontSize: '0.875rem' }}>
                      {results.error || 'Unknown error occurred'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-600)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--gray-900)' }}>Product Database</h3>
            <p>View and manage researched products</p>
          </div>
        )}

        {activeTab === 'competitors' && (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-600)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--gray-900)' }}>Competitor Intelligence</h3>
            <p>Track and analyze competitors</p>
          </div>
        )}

        {activeTab === 'trends' && (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-600)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📈</div>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--gray-900)' }}>Market Trends</h3>
            <p>Monitor industry trends and opportunities</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductResearch;
